import React from "react";
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  User,
  FileText,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  Building2,
  BarChart3,
  ExternalLink,
  CalendarDays,
  ShieldCheck,
  Briefcase,
  Globe,
} from "lucide-react";

const statusConfig = {
  FOUND: {
    icon: CheckCircle2,
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  PARTIAL: {
    icon: AlertCircle,
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
  ERROR: {
    icon: XCircle,
    className: "bg-red-50 text-red-700 border-red-200",
  },
  "NOT FOUND": {
    icon: AlertCircle,
    className: "bg-slate-100 text-slate-600 border-slate-200",
  },
};

const Section = ({ icon: Icon, title, children, action }) => {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
            <Icon size={18} />
          </div>

          <h2 className="text-sm font-semibold text-slate-900">
            {title}
          </h2>
        </div>

        {action}
      </div>

      <div className="p-5">{children}</div>
    </section>
  );
};

const InfoItem = ({ label, value, icon: Icon }) => {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
      <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
        {Icon && <Icon size={13} />}
        {label}
      </div>

      <div className="mt-1 break-words text-sm font-medium text-slate-800">
        {value || "—"}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const config = statusConfig[status] || statusConfig["NOT FOUND"];
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${config.className}`}
    >
      <Icon size={13} />
      {status}
    </span>
  );
};

const DataTable = ({ columns, data }) => {
  if (!data?.length) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-left text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {data.map((row, index) => (
              <tr
                key={index}
                className="transition hover:bg-slate-50/70"
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-4 py-3 text-sm text-slate-700"
                  >
                    {column.render
                      ? column.render(row, index)
                      : row[column.key] || "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AddressCard = ({ address }) => {
  const details = address.address_details;
  const insights = address.address_insights;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/70 px-4 py-3">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-slate-500" />

          <span className="text-sm font-semibold text-slate-800">
            Address #{address.address_rank}
          </span>
        </div>

        {insights?.address_quality?.level && (
          <span
            className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
              insights.address_quality.level === "VERY BAD"
                ? "bg-red-50 text-red-700"
                : insights.address_quality.level === "BAD"
                ? "bg-amber-50 text-amber-700"
                : "bg-emerald-50 text-emerald-700"
            }`}
          >
            {insights.address_quality.level}
          </span>
        )}
      </div>

      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <InfoItem
          label="Address"
          value={details.address}
          icon={MapPin}
        />

        <InfoItem
          label="Locality"
          value={details.locality}
        />

        <InfoItem
          label="City"
          value={details.city}
        />

        <InfoItem
          label="District"
          value={details.district}
        />

        <InfoItem
          label="State"
          value={details.state}
        />

        <InfoItem
          label="Country"
          value={details.country}
          icon={Globe}
        />

        <InfoItem
          label="Pincode"
          value={details.pincode}
        />

        <InfoItem
          label="Match Score"
          value={insights.address_match_score}
        />
      </div>

      {insights?.address_quality?.missing?.length > 0 && (
        <div className="border-t border-slate-100 px-4 py-3">
          <p className="mb-2 text-xs font-semibold text-slate-500">
            Missing Information
          </p>

          <div className="flex flex-wrap gap-2">
            {insights.address_quality.missing.map((item, index) => (
              <span
                key={index}
                className="rounded-lg bg-red-50 px-2.5 py-1.5 text-xs text-red-700"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MetadataGrid = ({ metadata }) => {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Object.entries(metadata || {}).map(([key, value]) => (
        <div
          key={key}
          className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50/60 p-3"
        >
          <div>
            <p className="text-xs font-semibold capitalize text-slate-700">
              {key.replaceAll("_", " ")}
            </p>

            {value.description && (
              <p className="mt-1 text-[11px] text-slate-400">
                {value.description}
              </p>
            )}
          </div>

          <StatusBadge status={value.status} />
        </div>
      ))}
    </div>
  );
};

const ApiResponseViewer = ({ response }) => {
  const data = response?.data;

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
        <AlertCircle className="mx-auto mb-3 text-slate-400" size={28} />
        <p className="text-sm text-slate-500">
          No structured response data available.
        </p>
      </div>
    );
  }

  const {
    profile,
    official_documents,
    contactability,
    addresses,
    pan_profile,
    employment_profile,
    business_profile,
    banking_profile,
    social_profile,
    insights,
    metadata,
    report,
  } = data;

  return (
    <div className="space-y-5">

      {/* Response Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50">
                <CheckCircle2
                  size={19}
                  className="text-emerald-600"
                />
              </div>

              <h1 className="text-base font-bold text-slate-900">
                API Response
              </h1>
            </div>

            <p className="mt-2 text-sm text-slate-500">
              {response.message || response.status?.message}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
              {response.status?.type?.toUpperCase()}
            </span>

            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600">
              {response.status?.code}
            </span>
          </div>
        </div>
      </div>

      {/* Profile */}
      {profile && (
        <Section icon={User} title="Profile">
          <div className="flex flex-col gap-5 sm:flex-row">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-slate-100">
              {profile.profile_image_url ? (
                <img
                  src={profile.profile_image_url}
                  alt={profile.full_name}
                  className="h-full w-full rounded-2xl object-cover"
                />
              ) : (
                <User size={32} className="text-slate-400" />
              )}
            </div>

            <div className="grid flex-1 gap-3 sm:grid-cols-2">
              <InfoItem
                label="Full Name"
                value={profile.full_name}
                icon={User}
              />

              <InfoItem
                label="Father's Name"
                value={profile.father_name}
              />

              <InfoItem
                label="Date of Birth"
                value={profile.date_of_birth}
                icon={CalendarDays}
              />

              <InfoItem
                label="Gender"
                value={profile.gender}
              />
            </div>
          </div>
        </Section>
      )}

      {/* Official Documents */}
      {official_documents?.length > 0 && (
        <Section icon={FileText} title="Official Documents">
          <DataTable
            data={official_documents}
            columns={[
              {
                key: "document",
                label: "Document",
              },
              {
                key: "document_id",
                label: "Document ID",
                render: (row) => (
                  <span className="font-mono font-semibold text-slate-800">
                    {row.document_id}
                  </span>
                ),
              },
            ]}
          />
        </Section>
      )}

      {/* Contactability */}
      {contactability && (
        <Section icon={Phone} title="Contactability">
          <div className="mb-5 grid gap-3 sm:grid-cols-2">
            <InfoItem
              label="Primary Mobile"
              value={contactability.mobile_number}
              icon={Phone}
            />

            <InfoItem
              label="Total Emails"
              value={contactability.emails?.length || 0}
              icon={Mail}
            />
          </div>

          {contactability.alternate_mobiles?.length > 0 && (
            <div className="mb-5">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Alternate Mobiles
              </h3>

              <DataTable
                data={contactability.alternate_mobiles}
                columns={[
                  {
                    key: "mobile_number",
                    label: "Mobile Number",
                    render: (row) => (
                      <div className="flex items-center gap-2 font-medium">
                        <Phone size={14} className="text-slate-400" />
                        {row.mobile_number}
                      </div>
                    ),
                  },
                  {
                    key: "reported_date",
                    label: "Reported Date",
                  },
                ]}
              />
            </div>
          )}

          {contactability.emails?.length > 0 && (
            <div>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Email Addresses
              </h3>

              <DataTable
                data={contactability.emails}
                columns={[
                  {
                    key: "email_id",
                    label: "Email",
                    render: (row) => (
                      <div className="flex items-center gap-2 font-medium">
                        <Mail size={14} className="text-slate-400" />
                        {row.email_id}
                      </div>
                    ),
                  },
                  {
                    key: "reported_date",
                    label: "Reported Date",
                  },
                ]}
              />
            </div>
          )}
        </Section>
      )}

      {/* Addresses */}
      {addresses?.length > 0 && (
        <Section
          icon={MapPin}
          title={`Addresses (${addresses.length})`}
        >
          <div className="space-y-4">
            {addresses.map((address, index) => (
              <AddressCard
                key={index}
                address={address}
              />
            ))}
          </div>
        </Section>
      )}

      {/* PAN */}
      {pan_profile && (
        <Section icon={ShieldCheck} title="PAN Profile">
          <div className="grid gap-3 sm:grid-cols-3">
            <InfoItem
              label="PAN"
              value={pan_profile.pan}
            />

            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-600">
                PAN Status
              </p>

              <div className="mt-2 flex items-center gap-2 text-sm font-bold text-emerald-700">
                <CheckCircle2 size={16} />
                {pan_profile.pan_status}
              </div>
            </div>

            <InfoItem
              label="Description"
              value={pan_profile.pan_status_description}
            />
          </div>
        </Section>
      )}

      {/* Banking */}
      {banking_profile && (
        <Section icon={Building2} title="Banking Profile">
          {banking_profile.bank_accounts?.length > 0 && (
            <DataTable
              data={banking_profile.bank_accounts}
              columns={[
                {
                  key: "bank_name",
                  label: "Bank",
                },
                {
                  key: "bank_account_number",
                  label: "Account Number",
                  render: (row) => (
                    <span className="font-mono">
                      {row.bank_account_number}
                    </span>
                  ),
                },
                {
                  key: "ifsc_code",
                  label: "IFSC",
                  render: (row) => (
                    <span className="font-mono font-semibold">
                      {row.ifsc_code}
                    </span>
                  ),
                },
                {
                  key: "branch_name",
                  label: "Branch",
                },
                {
                  key: "account_type",
                  label: "Type",
                },
                {
                  key: "refund_account",
                  label: "Refund",
                  render: (row) =>
                    row.refund_account ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                        <CheckCircle2 size={14} />
                        Yes
                      </span>
                    ) : (
                      "No"
                    ),
                },
              ]}
            />
          )}

          {banking_profile.upi_accounts?.length > 0 && (
            <div className="mt-5">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                UPI Accounts
              </h3>

              <DataTable
                data={banking_profile.upi_accounts}
                columns={Object.keys(
                  banking_profile.upi_accounts[0]
                ).map((key) => ({
                  key,
                  label: key.replaceAll("_", " "),
                }))}
              />
            </div>
          )}
        </Section>
      )}

      {/* Insights */}
      {insights && (
        <Section icon={BarChart3} title="Insights">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs text-slate-500">
                Total Addresses
              </p>

              <p className="mt-1 text-2xl font-bold text-slate-900">
                {insights.total_addresses}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs text-slate-500">
                Unique Addresses
              </p>

              <p className="mt-1 text-2xl font-bold text-slate-900">
                {insights.total_unique_addresses}
              </p>
            </div>
          </div>
        </Section>
      )}

      {/* Other Profiles */}
      {(employment_profile?.length > 0 ||
        business_profile?.length > 0 ||
        social_profile?.length > 0) && (
        <Section icon={Briefcase} title="Additional Profiles">
          <div className="space-y-3">
            {employment_profile?.length > 0 && (
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Employment Profile
                </p>

                <pre className="mt-3 overflow-auto text-xs text-slate-600">
                  {JSON.stringify(employment_profile, null, 2)}
                </pre>
              </div>
            )}

            {business_profile?.length > 0 && (
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Business Profile
                </p>

                <pre className="mt-3 overflow-auto text-xs text-slate-600">
                  {JSON.stringify(business_profile, null, 2)}
                </pre>
              </div>
            )}

            {social_profile?.length > 0 && (
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase text-slate-500">
                  Social Profile
                </p>

                <pre className="mt-3 overflow-auto text-xs text-slate-600">
                  {JSON.stringify(social_profile, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Metadata */}
      {metadata && (
        <Section icon={BarChart3} title="Source Metadata">
          <MetadataGrid metadata={metadata} />
        </Section>
      )}

      {/* Report */}
      {report && (
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                <FileText
                  size={20}
                  className="text-white"
                />
              </div>

              <div>
                <h2 className="text-sm font-semibold text-white">
                  Detailed PDF Report
                </h2>

                <p className="mt-1 text-xs text-slate-300">
                  View the complete generated profile report.
                </p>
              </div>
            </div>

            <a
              href={report}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-100"
            >
              <ExternalLink size={16} />
              View PDF Report
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiResponseViewer;